import { useCallback, useMemo } from 'react';

import { MedicalDeviceOrder } from '@dreryk/egabinet-ezwm-frontend-api';

import { LoaderSpinner, Message, MessageType } from '@dreryk/react-components';

import { visitPaths } from '~libs.desktop.shared.paths';

import {
   useLastUpdatedMedicalDeviceOrderQuery,
   useMedicalDeviceOrderQuery,
} from './medicalDeviceOrder/medicalDeviceOrder.hooks';
import { medicalDevicesTranslationKeys } from './translations';

type MedicalDevicesPanelLoaderRenderProps = {
   existingOrder: MedicalDeviceOrder | undefined;
   refetch: () => Promise<void>;
};

type MedicalDevicesPanelLoaderProps = {
   readonly visitId: number;
   readonly children: (renderProps: MedicalDevicesPanelLoaderRenderProps) => JSX.Element;
};

export const MedicalDevicesPanelLoader = (props: MedicalDevicesPanelLoaderProps): JSX.Element => {
   const { orderId } = visitPaths.useMedicalDeviceOrderSearchParams();
   const {
      data: lastUpdatedOrder,
      loading: lastUpdatedQueryLoading,
      refetch: refetchLastUpdatedOrder,
      error: lastUpdatedOrderError,
   } = useLastUpdatedMedicalDeviceOrderQuery(props.visitId, !orderId);
   const {
      data: medicalDeviceOrder,
      loading: medicalDeviceOrderQueryLoading,
      refetch: refetchMedicalDeviceOrder,
      error: medicalDeviceOrderError,
   } = useMedicalDeviceOrderQuery(lastUpdatedOrder?.externalId);

   const refetch = useCallback(async () => {
      if (orderId) {
         return refetchMedicalDeviceOrder();
      }
      return refetchLastUpdatedOrder();
   }, [refetchMedicalDeviceOrder, refetchLastUpdatedOrder, orderId]);

   const renderProps = useMemo(
      () => ({
         existingOrder: lastUpdatedOrder ?? medicalDeviceOrder ?? undefined,
         refetch: refetch,
      }),
      [lastUpdatedOrder, medicalDeviceOrder, refetch]
   );

   if (medicalDeviceOrderError || lastUpdatedOrderError) {
      return (
         <Message
            testId={'medicalDeviceOrderQueryError'}
            type={MessageType.NEGATIVE}
            label={medicalDevicesTranslationKeys.draftFetchingErrorOccurred}
            displayIndicator
         />
      );
   }

   if (lastUpdatedQueryLoading || medicalDeviceOrderQueryLoading) {
      return <LoaderSpinner />;
   }

   return props.children(renderProps);
};
